import React, { useState, useEffect } from 'react';
import {
  BarChart3, Calendar, DollarSign, Users, Store, RefreshCw,
  FileText, Download, ArrowDown
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import adminService, {
  DailyReport,
  WeeklyReport,
  MonthlyReport,
  UserGrowthData,
  RevenueAnalyticsData,
  SettlementSummaryReport,
  BusinessPerformanceReport,
  ExportFormat
} from '../../services/admin/adminService';
import { formatCurrency } from '../../utils/formatters';

type ViewMode = 'daily' | 'weekly' | 'monthly' | 'custom' | 'user-growth' | 'revenue' | 'settlements' | 'business';

const ReportsAnalytics: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('daily');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Report Data States
  const [dailyReport, setDailyReport] = useState<DailyReport | null>(null);
  const [weeklyReport, setWeeklyReport] = useState<WeeklyReport | null>(null);
  const [monthlyReport, setMonthlyReport] = useState<MonthlyReport | null>(null);
  const [userGrowthData, setUserGrowthData] = useState<UserGrowthData[]>([]);
  const [revenueData, setRevenueData] = useState<RevenueAnalyticsData[]>([]);
  const [settlementReport, setSettlementReport] = useState<SettlementSummaryReport | null>(null);
  const [businessReport, setBusinessReport] = useState<BusinessPerformanceReport | null>(null);

  // Filters
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day');
  const [businessId, setBusinessId] = useState('');

  function getDefaultStartDate() {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  }

  useEffect(() => {
    loadReportData();
  }, [viewMode]);

  const loadReportData = async () => {
    setLoading(true);
    try {
      switch (viewMode) {
        case 'daily':
          await loadDailyReport();
          break;
        case 'weekly':
          await loadWeeklyReport();
          break;
        case 'monthly':
          await loadMonthlyReport();
          break;
        case 'user-growth':
          await loadUserGrowthReport();
          break;
        case 'revenue':
          await loadRevenueAnalytics();
          break;
        case 'settlements':
          await loadSettlementReport();
          break;
        case 'business':
          if (businessId) {
            await loadBusinessReport();
          }
          break;
      }
    } catch (error: any) {
      console.error('Error loading report:', error);
      toast.error(error.response?.data?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const loadDailyReport = async () => {
    const report = await adminService.getDailyReport({ date: selectedDate });
    setDailyReport(report);
  };

  const loadWeeklyReport = async () => {
    const report = await adminService.getWeeklyReport({ start_date: startDate, end_date: endDate });
    setWeeklyReport(report);
  };

  const loadMonthlyReport = async () => {
    const report = await adminService.getMonthlyReport({ month: selectedMonth, year: selectedYear });
    setMonthlyReport(report);
  };

  const loadUserGrowthReport = async () => {
    const data = await adminService.getUserGrowthReport({ start_date: startDate, end_date: endDate, group_by: groupBy });
    setUserGrowthData(data);
  };

  const loadRevenueAnalytics = async () => {
    const data = await adminService.getRevenueAnalytics({ start_date: startDate, end_date: endDate, group_by: groupBy });
    setRevenueData(data);
  };


  const loadSettlementReport = async () => {
    const data = await adminService.getSettlementSummary({ start_date: startDate, end_date: endDate });
    setSettlementReport(data);
  };

  const loadBusinessReport = async () => {
    const data = await adminService.getBusinessPerformanceReport({
      business_id: Number(businessId),
      start_date: startDate,
      end_date: endDate
    });
    setBusinessReport(data);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadReportData();
    setRefreshing(false);
    toast.success('Report refreshed');
  };

  const handleExport = async (format: ExportFormat) => {
    try {
      let exportParams: any = { format };

      switch (viewMode) {
        case 'daily':
          exportParams.report_type = 'daily';
          exportParams.date = selectedDate;
          break;
        case 'weekly':
          exportParams.report_type = 'weekly';
          exportParams.start_date = startDate;
          exportParams.end_date = endDate;
          break;
        case 'monthly':
          exportParams.report_type = 'monthly';
          exportParams.date = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
          break;
        case 'settlements':
          exportParams.report_type = 'settlement';
          exportParams.start_date = startDate;
          exportParams.end_date = endDate;
          break;
        default:
          exportParams.report_type = 'custom';
          exportParams.start_date = startDate;
          exportParams.end_date = endDate;
      }

      const data = await adminService.exportReport(exportParams);

      // Create download link
      const blob = new Blob([data], { type: getContentType(format) });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report_${viewMode}_${Date.now()}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Report exported as ${format.toUpperCase()}`);
    } catch (error: any) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    }
  };

  const getContentType = (format: ExportFormat) => {
    const types = {
      pdf: 'application/pdf',
      excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      csv: 'text/csv',
      json: 'application/json'
    };
    return types[format];
  };

  const renderSimpleBarChart = (data: { label: string; value: number; max: number }[]) => {
    if (data.length === 0) return <p className="text-center text-secondary-500 py-8">No data available</p>;

    return (
      <div className="space-y-4">
        {data.map((item, index) => (
          <div key={index}>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-secondary-700">{item.label}</span>
              <span className="text-sm font-semibold text-secondary-900">{item.value}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-primary-500 h-3 rounded-full transition-all duration-300"
                style={{ width: `${(item.value / item.max) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderLineChart = (data: { label: string; value: number }[]) => {
    if (data.length === 0) return <p className="text-center text-secondary-500 py-8">No data available</p>;

    const max = Math.max(...data.map(d => d.value));
    const height = 200;
    const width = 100;

    return (
      <div className="relative" style={{ height: `${height}px` }}>
        <div className="flex items-end justify-around h-full">
          {data.map((item, index) => {
            const barHeight = max > 0 ? (item.value / max) * (height - 40) : 0;
            return (
              <div key={index} className="flex flex-col items-center" style={{ width: `${width / data.length}%` }}>
                <div className="text-xs font-semibold text-primary-600 mb-1">{item.value}</div>
                <div
                  className="bg-primary-500 w-full rounded-t transition-all duration-300 hover:bg-primary-600"
                  style={{ height: `${barHeight}px`, minHeight: '4px' }}
                />
                <div className="text-xs text-secondary-600 mt-2 text-center truncate w-full px-1">
                  {item.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Reports & Analytics</h1>
          <p className="text-secondary-600 mt-1">Comprehensive insights and data visualization</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
          <div className="relative group">
            <button className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2">
              <Download size={18} />
              Export
            </button>
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-2 hidden group-hover:block z-10">
              <button onClick={() => handleExport('pdf')} className="w-full text-left px-4 py-2 hover:bg-gray-100">Export as PDF</button>
              <button onClick={() => handleExport('excel')} className="w-full text-left px-4 py-2 hover:bg-gray-100">Export as Excel</button>
              <button onClick={() => handleExport('csv')} className="w-full text-left px-4 py-2 hover:bg-gray-100">Export as CSV</button>
              <button onClick={() => handleExport('json')} className="w-full text-left px-4 py-2 hover:bg-gray-100">Export as JSON</button>
            </div>
          </div>
        </div>
      </div>

      {/* View Mode Tabs */}
      <div className="flex flex-wrap gap-2 bg-white p-2 rounded-lg border border-gray-200">
        {[
          { mode: 'daily' as ViewMode, label: 'Daily', icon: Calendar },
          { mode: 'weekly' as ViewMode, label: 'Weekly', icon: Calendar },
          { mode: 'monthly' as ViewMode, label: 'Monthly', icon: Calendar },
          { mode: 'user-growth' as ViewMode, label: 'User Growth', icon: Users },
          { mode: 'business' as ViewMode, label: 'Business', icon: Store }
        ].map(({ mode, label, icon: Icon }) => (
          <button
            key={mode}
            onClick={() => setViewMode(mode)}
            className={`px-4 py-2 rounded-md transition-colors flex items-center gap-2 ${viewMode === mode
                ? 'bg-primary-500 text-white'
                : 'text-secondary-700 hover:bg-gray-100'
              }`}
          >
            <Icon size={18} />
            {label}
          </button>
        ))}
      </div>

      {/* Filters Section */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-wrap gap-4 items-end">
          {viewMode === 'daily' && (
            <>
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-secondary-700 mb-2">Select Date</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <button
                onClick={loadDailyReport}
                className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                Generate Report
              </button>
            </>
          )}

          {(viewMode === 'weekly' || viewMode === 'user-growth' || viewMode === 'revenue') && (
            <>
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-secondary-700 mb-2">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-secondary-700 mb-2">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              {(viewMode === 'user-growth' || viewMode === 'revenue') && (
                <div className="flex-1 min-w-[150px]">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Group By</label>
                  <select
                    value={groupBy}
                    onChange={(e) => setGroupBy(e.target.value as 'day' | 'week' | 'month')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="day">Day</option>
                    <option value="week">Week</option>
                    <option value="month">Month</option>
                  </select>
                </div>
              )}
              <button
                onClick={loadReportData}
                className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                Generate Report
              </button>
            </>
          )}

          {viewMode === 'monthly' && (
            <>
              <div className="flex-1 min-w-[150px]">
                <label className="block text-sm font-medium text-secondary-700 mb-2">Month</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                    <option key={month} value={month}>
                      {new Date(2000, month - 1).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1 min-w-[150px]">
                <label className="block text-sm font-medium text-secondary-700 mb-2">Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={loadMonthlyReport}
                className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                Generate Report
              </button>
            </>
          )}

          {viewMode === 'business' && (
            <>
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-secondary-700 mb-2">Business ID</label>
                <input
                  type="number"
                  value={businessId}
                  onChange={(e) => setBusinessId(e.target.value)}
                  placeholder="Enter business ID"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-secondary-700 mb-2">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-secondary-700 mb-2">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <button
                onClick={loadReportData}
                disabled={!businessId}
                className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Generate Report
              </button>
            </>
          )}
        </div>
      </div>

      {/* Report Content */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <RefreshCw className="animate-spin text-primary-500" size={48} />
        </div>
      ) : (
        <>
          {/* Daily Report */}
          {viewMode === 'daily' && dailyReport && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-secondary-600">Total Transactions</p>
                      <p className="text-3xl font-bold text-secondary-900 mt-1">{dailyReport.total_transactions}</p>
                    </div>
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <BarChart3 size={28} className="text-blue-600" />
                    </div>
                  </div>
                </div>


                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-secondary-600">New Users</p>
                      <p className="text-3xl font-bold text-secondary-900 mt-1">{dailyReport.new_users}</p>
                    </div>
                    <div className="p-3 bg-green-100 rounded-lg">
                      <Users size={28} className="text-green-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-secondary-600">New Businesses</p>
                      <p className="text-3xl font-bold text-secondary-900 mt-1">{dailyReport.new_businesses}</p>
                    </div>
                    <div className="p-3 bg-purple-100 rounded-lg">
                      <Store size={28} className="text-purple-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-secondary-600">Failed Transactions</p>
                      <p className="text-3xl font-bold text-red-600 mt-1">{dailyReport.failed_transactions}</p>
                    </div>
                    <div className="p-3 bg-red-100 rounded-lg">
                      <ArrowDown size={28} className="text-red-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-secondary-600">Report Date</p>
                      <p className="text-lg font-semibold text-secondary-900 mt-1">
                        {new Date(dailyReport.report_date).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-100 rounded-lg">
                      <Calendar size={28} className="text-gray-600" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Weekly Report */}
          {viewMode === 'weekly' && weeklyReport && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <p className="text-sm text-secondary-600">Total Transactions</p>
                  <p className="text-3xl font-bold text-secondary-900 mt-1">{weeklyReport.total_transactions}</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <p className="text-sm text-secondary-600">New Users</p>
                  <p className="text-3xl font-bold text-secondary-900 mt-1">{weeklyReport.new_users}</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <p className="text-sm text-secondary-600">New Businesses</p>
                  <p className="text-3xl font-bold text-secondary-900 mt-1">{weeklyReport.new_businesses}</p>
                </div>
              </div>
            </div>
          )}

          {/* Monthly Report */}
          {viewMode === 'monthly' && monthlyReport && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <p className="text-sm text-secondary-600">Total Transactions</p>
                  <p className="text-3xl font-bold text-secondary-900 mt-1">{monthlyReport.total_transactions}</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <p className="text-sm text-secondary-600">New Users</p>
                  <p className="text-3xl font-bold text-secondary-900 mt-1">{monthlyReport.new_users}</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <p className="text-sm text-secondary-600">New Businesses</p>
                  <p className="text-3xl font-bold text-secondary-900 mt-1">{monthlyReport.new_businesses}</p>
                </div>
              </div>
            </div>
          )}

          {/* User Growth Chart */}
          {viewMode === 'user-growth' && (
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-xl font-semibold text-secondary-900 mb-6">User Growth Trend</h3>
              {renderLineChart(userGrowthData.map(d => ({
                label: new Date(d.period).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                value: d.new_users
              })))}
            </div>
          )}



          {/* Settlement Report */}
          {viewMode === 'settlements' && settlementReport && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <p className="text-sm text-secondary-600">Total Settlements</p>
                  <p className="text-2xl font-bold text-secondary-900 mt-1">
                    {settlementReport.summary.total_settlements}
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <p className="text-sm text-secondary-600">Total Amount</p>
                  <p className="text-2xl font-bold text-primary-600 mt-1">
                    {formatCurrency(settlementReport.summary.total_settlement_amount, 'INR', 'en-IN', '₹')}
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <p className="text-sm text-secondary-600">Net Amount</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">
                    {formatCurrency(settlementReport.summary.total_net_amount, 'INR', 'en-IN', '₹')}
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <p className="text-sm text-secondary-600">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600 mt-1">
                    {settlementReport.summary.pending_settlements}
                  </p>
                </div>
              </div>

              {settlementReport.business_breakdown.length > 0 && (
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <h3 className="text-xl font-semibold text-secondary-900 mb-6">Business Breakdown</h3>
                  {renderSimpleBarChart(settlementReport.business_breakdown.map(b => ({
                    label: b.business_name,
                    value: parseFloat(b.total_amount),
                    max: Math.max(...settlementReport.business_breakdown.map(x => parseFloat(x.total_amount)))
                  })))}
                </div>
              )}
            </div>
          )}

          {/* Business Performance Report */}
          {viewMode === 'business' && businessReport && (
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-2xl font-bold text-secondary-900 mb-6">{businessReport.business_name}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div>
                    <p className="text-sm text-secondary-600">Total Transactions</p>
                    <p className="text-3xl font-bold text-secondary-900 mt-1">{businessReport.total_transactions}</p>
                  </div>
                  <div>
                    <p className="text-sm text-secondary-600">Avg Transaction Value</p>
                    <p className="text-3xl font-bold text-secondary-900 mt-1">
                      {businessReport.avg_transaction_value}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-secondary-600">Unique Customers</p>
                    <p className="text-3xl font-bold text-secondary-900 mt-1">{businessReport.unique_customers}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* No Data Message */}
          {((viewMode === 'user-growth' && userGrowthData.length === 0) ||
            (viewMode === 'revenue' && revenueData.length === 0) ||
            (viewMode === 'business' && !businessReport)) && !loading && (
              <div className="bg-white p-12 rounded-lg shadow-sm border border-gray-200 text-center">
                <FileText size={64} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-900 mb-2">No Data Available</h3>
                <p className="text-secondary-600">
                  {viewMode === 'business'
                    ? 'Please enter a valid Business ID and generate the report.'
                    : 'No data found for the selected date range. Try adjusting your filters.'}
                </p>
              </div>
            )}
        </>
      )}
    </div>
  );
};

export default ReportsAnalytics;

